import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, faTimes, faFileExcel, faSpinner, faCheckCircle, 
  faExclamationTriangle, faDownload, faEye 
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../ui/ToastProvider';
import { importArticlesFromExcel, validateExcelData, validateExcelFile, downloadArticleTemplate, downloadFailedImports } from '../../services/articleApi';
import { parseImportFile } from '../../utils/excelParser';

interface ArticleImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ImportResult {
  successful: number;
  failed: number;
  errors: Array<{
    row: number;
    articleNumber?: string;
    error: string;
    data?: any;
  }>;
  created?: any[];
  updated?: any[];
}

const ArticleImportModal: React.FC<ArticleImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const { showSuccess, showError, showInfo } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<string>('');
  const [startTime, setStartTime] = useState<Date | null>(null);

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension || '')) {
      showError('Nur Excel (.xlsx, .xls) oder CSV-Dateien sind erlaubt');
      return;
    }

    // Validate file size (max 50MB for large imports)
    if (file.size > 50 * 1024 * 1024) {
      showError('Datei ist zu groß. Maximum 50MB erlaubt.');
      return;
    }

    setImportFile(file);
    setImportResult(null);
    setParsedData([]);
    setShowPreview(false);

    // Parse and validate file
    await parseAndValidateFile(file);
  };

  const parseAndValidateFile = async (file: File) => {
    try {
      setValidating(true);
      
      // Parse file
      const parseResult = await parseImportFile(file);
      
      if (parseResult.errors.length > 0) {
        showError(`Fehler beim Parsen der Datei: ${parseResult.errors.join(', ')}`);
        return;
      }

      setParsedData(parseResult.data);
      
      // Check if file is too large for JSON validation (>10,000 entries)
      if (parseResult.data.length > 10000) {
        showInfo(`Große Datei erkannt (${parseResult.data.length} Einträge). Verwende File-Upload-Validierung...`);
        
        try {
          // Use file-based validation for large files
          const validationResult = await validateExcelFile(file);
          
          if (validationResult.failed > 0) {
            setImportResult(validationResult);
            showInfo(`${validationResult.successful} gültige, ${validationResult.failed} fehlerhafte Einträge gefunden`);
          } else {
            showSuccess(`${parseResult.data.length} Artikel zur Importierung bereit`);
          }
        } catch (fileError: any) {
          console.error('File upload validation failed:', fileError);
          throw fileError;
        }
      } else {
        // Use regular JSON validation for smaller files
        try {
          const validationResult = await validateExcelData(parseResult.data);
          
          if (validationResult.failed > 0) {
            setImportResult(validationResult);
            showInfo(`${validationResult.successful} gültige, ${validationResult.failed} fehlerhafte Einträge gefunden`);
          } else {
            showSuccess(`${parseResult.data.length} Artikel zur Importierung bereit`);
          }
        } catch (jsonError: any) {
          // If JSON validation fails due to size (413), fall back to file upload
          if (jsonError.status === 413 || jsonError.response?.status === 413) {
            console.log('JSON zu groß, verwende File-Upload...');
            showInfo('Datei zu groß für JSON-Übertragung. Verwende File-Upload-Validierung...');
            
            const validationResult = await validateExcelFile(file);
            
            if (validationResult.failed > 0) {
              setImportResult(validationResult);
              showInfo(`${validationResult.successful} gültige, ${validationResult.failed} fehlerhafte Einträge gefunden`);
            } else {
              showSuccess(`${parseResult.data.length} Artikel zur Importierung bereit`);
            }
          } else {
            throw jsonError;
          }
        }
      }
      
    } catch (error: any) {
      console.error('Error parsing/validating file:', error);
      
      // Enhanced error handling for 413 errors with server messages
      if (error.status === 413 || error.response?.status === 413) {
        const responseData = error.response?.data;
        if (responseData?.message && responseData?.recommendFileUpload) {
          showError(`${responseData.message}\n\n${responseData.hint || ''}`);
          console.log('Server empfiehlt File-Upload für', responseData.totalRows, 'Einträge');
        } else {
          showError('Datei zu groß für JSON-Übertragung. Verwende File-Upload-Import für große Dateien.');
        }
      } else {
        showError(error.message || 'Fehler beim Verarbeiten der Datei');
      }
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!parsedData.length) {
      showError('Keine Daten zum Importieren verfügbar');
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);
      setStartTime(new Date());
      setImportStatus(`Beginne Import von ${parsedData.length} Artikeln...`);
      
      // Simulate progress updates during import
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) return prev; // Stop at 90% until real completion
          return prev + Math.random() * 10;
        });
      }, 1000);
      
      // Update status messages
      const statusMessages = [
        'Validiere Artikeldaten...',
        'Suche Lieferanten...',
        'Verarbeite Artikelfelder...',
        'Speichere in Datenbank...',
        'Erstelle Verknüpfungen...'
      ];
      
      let messageIndex = 0;
      const statusInterval = setInterval(() => {
        if (messageIndex < statusMessages.length) {
          setImportStatus(statusMessages[messageIndex]);
          messageIndex++;
        }
      }, 3000);
      
      const result = await importArticlesFromExcel(parsedData);
      
      // Debug logging
      console.log('🔍 Import result received:', result);
      console.log('🔍 Import result type:', typeof result);
      console.log('🔍 Import result keys:', Object.keys(result || {}));
      
      // Clear intervals
      clearInterval(progressInterval);
      clearInterval(statusInterval);
      
      // Complete progress
      setImportProgress(100);
      setImportStatus('Import abgeschlossen!');
      setImportResult(result);
      
      if (result.successful > 0) {
        showSuccess(`${result.successful} Artikel erfolgreich importiert`);
        // onImportComplete() nur aufrufen wenn KEINE Fehler vorliegen
        if (result.failed === 0) {
          onImportComplete();
        }
      }
      
      if (result.failed > 0) {
        showError(`${result.failed} Artikel konnten nicht importiert werden`);
        // Modal offen lassen, damit Benutzer die Fehler sehen und Excel herunterladen kann
      }
      
    } catch (error: any) {
      console.error('Error importing articles:', error);
      setImportStatus('Fehler beim Import!');
      setImportProgress(0);
      showError(error.message || 'Fehler beim Importieren der Artikel');
    } finally {
      setImporting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDownloadFailedImports = async (errors: any[]) => {
    try {
      console.log('📁 Downloading failed imports Excel...', errors.length);
      showInfo('Excel-Datei wird erstellt...');
      
      // Use the articleApi function that handles token refresh automatically
      const blob = await downloadFailedImports(errors);
      
      if (blob.size === 0) {
        throw new Error('Leere Datei erhalten');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename with timestamp
      const filename = `fehlgeschlagene_importe_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.xlsx`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess(`✅ Excel-Datei "${filename}" mit ${errors.length} fehlgeschlagenen Importen erfolgreich heruntergeladen!`);
    } catch (error: any) {
      console.error('Failed imports download error:', error);
      showError(`❌ Fehler beim Download: ${error.message || error.response?.data?.message || 'Unbekannter Fehler'}`);
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await downloadArticleTemplate('excel');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'article_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Excel-Vorlage erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Template download error:', error);
      showError('Fehler beim Download der Excel-Vorlage');
    }
  };

  const downloadCSVTemplate = async () => {
    try {
      const blob = await downloadArticleTemplate('csv');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'article_import_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('CSV-Vorlage erfolgreich heruntergeladen');
    } catch (error) {
      console.error('CSV template download error:', error);
      showError('Fehler beim Download der CSV-Vorlage');
    }
  };

  const downloadLegacyTemplate = () => {
    // Create comprehensive template data with all supported fields (Legacy Format)
    const templateData = [
      {
        // **GRUNDDATEN (ERFORDERLICH)**
        '_Value': 'ART001',
        '#BCItemDescription': 'Beispiel Artikel Name',
        'description': 'Detaillierte Artikelbeschreibung',
        'BrandName': 'Beispiel Marke',
        
        // **IDENTIFIKATION & CODES**
        'MarkantbaseUnitGtin': '1234567890123',
        'gtin': '1234567890123',
        'itemNumber': 'ITEM001',
        'supplierAssignedId': '12345',
        'intrastatNumber': '12345678',
        'creditorNumber': '750288',
        'itemNumberCheckDigit': '4',
        
        // **INHALT & EINHEITEN**
        'content': '500',
        'contentUnit': 'g',
        'unit': 'Stück',
        'pieceWeightArticle': 'false',
        'netContent': '450',
        'netContentUom': 'g',
        
        // **LIEFERANTEN-INFO**
        'mainSupplierCode': 'SUP001',
        'mainSupplierGLN': '9001939000001',
        'supplierContact': 'Firma Beispiel GmbH',
        
        // **EK & KATEGORIEN**
        'ekCode': '4',
        'departmentCode': '3701',
        'storageZone': 'Non Food',
        
        // **HERKUNFT & QUALITÄT**
        'countryOfOrigin': '040',
        'reusableCode': 'kein Mehrweg/Einweg',
        
        // **ECR CODES**
        'ecrLevel1': '12',
        'ecrCode': '120400010',
        
        // **ALKOHOL**
        'alcoholContent': '0',
        
        // **LAUFZEITEN**
        'minTermIncoming': '0',
        'minTermOutgoing': '0',
        
        // **STATUS & FREIGABEN**
        'goldenRecord': 'true',
        'productCompletePct': '36',
        'trafficLightStatus': '#00FF00',
        'ghpApproval': 'true',
        'approvedERP': 'true',
        'approvedPrint': 'true',
        
        // **WEITERE FELDER**
        'itemType': 'Lieferantenartikel',
        'bcItemDescription': 'Beispiel Artikel Name',
        'bcSearchDescription': 'Beispiel Artikel Name'
      }
    ];

    // Convert to CSV
    const headers = Object.keys(templateData[0]);
    const csvContent = [
      headers.join(','),
      templateData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(',')).join('\n')
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'artikel_import_template_vollstaendig.csv';
    link.click();
  };

  const downloadMinimalTemplate = () => {
    // Use backend API for minimal template download (same as main template now)
    downloadCSVTemplate();
  };

  const handleClose = () => {
    setImportFile(null);
    setImportResult(null);
    setParsedData([]);
    setShowPreview(false);
    setValidating(false);
    setImporting(false);
    setImportProgress(0);
    setImportStatus('');
    setStartTime(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faUpload} className="text-2xl text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Artikel Import</h2>
                <p className="text-sm text-gray-600">Excel oder CSV-Datei mit Artikeldaten importieren</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="p-6">
            {/* Template Download */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Import-Vorlage & Hinweise</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Laden Sie die WEDL-konforme Vorlage mit ItemNumber, MarkantbaseUnitGtin, WEDLBezeichnung1/2, BrandName, WEDLSupplierCode, etc. herunter
                  </p>
                  <div className="text-xs text-blue-600 mt-2 space-y-1">
                    <div><strong>WEDL Pflichtfelder:</strong> ItemNumber, MarkantbaseUnitGtin, WEDLBezeichnung1, BrandName, WEDLSupplierCode</div>
                    <div><strong>✅ Alle anderen WEDL-Spalten sind optional</strong> und können leer bleiben</div>
                    <div><strong>🔄 Perfion-Sync ergänzt fehlende Daten</strong> automatisch über ItemNumber</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={downloadMinimalTemplate}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Minimal-Vorlage
                  </button>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    WEDL Excel Template
                  </button>
                  <button
                    onClick={downloadCSVTemplate}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    WEDL CSV Template
                  </button>
                </div>
              </div>
              <div className="mt-3 p-3 bg-blue-100 rounded text-xs text-blue-700">
                <strong>💡 Tipp:</strong> Verwenden Sie die WEDL-Feldnamen für optimale Integration. 
                Der automatische Perfion-Import ergänzt später alle weiteren Daten wie GTIN, Marken, 
                Nährwerte etc. basierend auf der ItemNumber.
              </div>
            </div>

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver
                  ? 'border-blue-500 bg-blue-50'
                  : importFile
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 bg-gray-50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {importFile ? (
                <div className="space-y-4">
                  <FontAwesomeIcon icon={faFileExcel} className="text-4xl text-green-600" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">{importFile.name}</p>
                    <p className="text-sm text-gray-600">
                      {(importFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  {validating && (
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      <span>Validiere Daten...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <FontAwesomeIcon icon={faUpload} className="text-4xl text-gray-400" />
                  <div>
                    <p className="text-lg font-medium text-gray-900">
                      Datei hier ablegen oder klicken zum Auswählen
                    </p>
                    <p className="text-sm text-gray-600">
                      Unterstützte Formate: .xlsx, .xls, .csv (max. 10MB)
                    </p>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Datei auswählen
                  </button>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>

            {/* Preview Data */}
            {parsedData.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Vorschau ({parsedData.length} Einträge)
                  </h3>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    {showPreview ? 'Ausblenden' : 'Anzeigen'}
                  </button>
                </div>

                {showPreview && (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Artikel-Nr.
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Name
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            GTIN
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Marke
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            EK-Code
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Kreditor-Nr.
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Inhalt
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parsedData.slice(0, 5).map((item, index) => {
                          // Ermittle den Namen aus verschiedenen Feldern
                          const articleName = item['#BCItemDescription'] || item.WEDLBezeichnung1_DE || item.bcItemDescription || 'N/A';
                          const gtin = item.MarkantbaseUnitGtin || item.gtin || 'N/A';
                          const brand = item.BrandName || 'N/A';
                          const ekCode = item.WEDLEIK || item.ekCode || 'N/A';
                          const creditorNumber = item['#RelWEDLHauptlieferantKreditorNr'] || item.creditorNumber || 'N/A';
                          const content = item.WEDLInhalt || item.content || 'N/A';
                          const contentUnit = item.WEDLInhaltEinheit_DE || item.contentUnit || '';
                          const contentDisplay = content !== 'N/A' && contentUnit ? `${content} ${contentUnit}` : content;
                          
                          return (
                            <tr key={index}>
                              <td className="px-3 py-2 text-sm text-gray-900 font-mono">{item._Value || 'N/A'}</td>
                              <td className="px-3 py-2 text-sm text-gray-900" title={articleName}>{articleName.length > 30 ? articleName.substring(0, 30) + '...' : articleName}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 font-mono">{gtin}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{brand}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 font-mono">{ekCode}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 font-mono">{creditorNumber}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{contentDisplay}</td>
                              <td className="px-3 py-2 text-sm">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  Bereit
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {parsedData.length > 5 && (
                      <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50">
                        ... und {parsedData.length - 5} weitere Einträge
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Import Progress */}
            {importing && (
              <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-blue-900">Import wird durchgeführt...</h3>
                  <div className="text-sm text-blue-700">
                    {startTime && (
                      <span>
                        Dauer: {Math.floor((new Date().getTime() - startTime.getTime()) / 1000)}s
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">Fortschritt</span>
                    <span className="text-sm text-blue-600">{Math.round(importProgress)}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-500 relative overflow-hidden"
                      style={{ width: `${importProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
                    </div>
                  </div>
                </div>
                
                {/* Status Message */}
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700">{importStatus}</span>
                </div>
                
                {/* Article Count Info */}
                <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">{parsedData.length}</div>
                    <div className="text-xs text-gray-600">Gesamt</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-lg font-bold text-green-600">
                      {Math.round((importProgress / 100) * parsedData.length)}
                    </div>
                    <div className="text-xs text-gray-600">Verarbeitet</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-lg font-bold text-gray-600">
                      {parsedData.length - Math.round((importProgress / 100) * parsedData.length)}
                    </div>
                    <div className="text-xs text-gray-600">Ausstehend</div>
                  </div>
                </div>
              </div>
            )}



            {/* Import Results */}
            {importResult && (
              <div className="mt-6 space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Import-Ergebnis</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                      <span className="font-medium text-green-900">Erfolgreich</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600 mt-1">{importResult.successful}</p>
                  </div>
                  
                  {importResult.failed > 0 && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                        <span className="font-medium text-red-900">Fehlgeschlagen</span>
                      </div>
                      <p className="text-2xl font-bold text-red-600 mt-1">{importResult.failed}</p>
                    </div>
                  )}
                </div>

                {/* Error Details with Download Button */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        Fehlermeldungen ({importResult.errors.length}):
                      </h4>
                      <button
                        onClick={() => handleDownloadFailedImports(importResult.errors)}
                        className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-2 shadow-md"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Excel mit Fehlern herunterladen
                      </button>
                    </div>
                    
                    {/* Error Summary */}
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>💡 Häufigste Probleme:</strong> Die meisten Fehler entstehen durch fehlende Lieferanten in der Datenbank. 
                        Laden Sie die Excel-Datei herunter, um alle betroffenen Artikel und deren Lieferanten-Codes zu sehen.
                      </p>
                    </div>

                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      {importResult.errors.slice(0, 10).map((error, index) => (
                        <div key={index} className="p-3 border-b border-gray-200 last:border-b-0">
                          <p className="text-sm text-red-600">
                            <span className="font-medium">Artikel {error.articleNumber} (Zeile {error.row}):</span>
                          </p>
                          <p className="text-xs text-gray-600 mt-1">{error.error}</p>
                        </div>
                      ))}
                      {importResult.errors.length > 10 && (
                        <div className="p-3 bg-gray-50 text-center">
                          <p className="text-sm text-gray-600">
                            ... und {importResult.errors.length - 10} weitere Fehler. 
                            Laden Sie die Excel-Datei für alle Details herunter.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Schließen
            </button>
            
            {/* Import Button oder Fertig Button */}
            {importResult ? (
              <button
                onClick={() => {
                  onImportComplete(); // Refresh der Artikel-Liste
                  handleClose(); // Modal schließen
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Import abgeschlossen - Fertig
              </button>
            ) : parsedData.length > 0 ? (
              <button
                onClick={handleImport}
                disabled={importing || validating}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Importiere... ({Math.round(importProgress)}%)
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} />
                    Import starten ({parsedData.length} Artikel)
                  </>
                )}
              </button>
            ) : null}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ArticleImportModal;